// Shape geometry helpers for keypoint markers (spec table: circle/square/star)
// and per-edge side classification for skeleton-line coloring.

import type { KeypointName } from '../types';

// Ratio of inner to outer radius for a regular 5-point star.
const STAR_INNER_RATIO = 0.382;

/**
 * SVG `points` string for a 5-point star centered at (cx, cy) with outer
 * radius r. The first point sits at the top (12 o'clock). Reused by the
 * canvas overlay and the small panel/legend icons.
 */
export function starPoints(cx: number, cy: number, r: number): string {
  const inner = r * STAR_INNER_RATIO;
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? r : inner;
    // Start at -90° (top) and step 36° per vertex.
    const angle = (-90 + i * 36) * (Math.PI / 180);
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    pts.push(`${x.toFixed(3)},${y.toFixed(3)}`);
  }
  return pts.join(' ');
}

/**
 * Classify a skeleton edge by body side for line coloring:
 *   - 'left'  if an endpoint is a left_* joint and neither is right_*
 *   - 'right' if an endpoint is a right_* joint and neither is left_*
 *   - 'cross' otherwise (e.g. left_shoulder–right_shoulder)
 * pelvis is side-neutral, so left_shoulder–pelvis → left, right_shoulder–pelvis → right.
 */
export function edgeSide(a: KeypointName, b: KeypointName): 'left' | 'right' | 'cross' {
  const hasLeft = a.startsWith('left') || b.startsWith('left');
  const hasRight = a.startsWith('right') || b.startsWith('right');
  if (hasLeft && !hasRight) return 'left';
  if (hasRight && !hasLeft) return 'right';
  return 'cross';
}
